import { useQuery } from "react-query";
import { useApi, apiResolver } from "@/api/useApi";
import {
  ExploreInteractionRow,
  ExploreInteractionType,
} from "@/v2/entities/explore/Explore.model";
import {
  ConversationInTraining,
  TicketInTraining,
} from "@/routes/simulator/pendingReview/types";
import { SendInteractionToTraining } from "@/routes/simulator-live-mode/sendTicketToTraining";
import { useMemo } from "react";
import {
  InteractionInTrainingStatus,
  InteractionType,
} from "@/routes/simulator-live-mode/types";
import { Badge, Spinner } from "@radix-ui/themes";

interface SendTicketToTrainingProps {
  source: ExploreInteractionType;
  interaction: ExploreInteractionRow;
}

export function SendTicketToTraining({
  source,
  interaction,
}: SendTicketToTrainingProps) {
  const { api } = useApi();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["interaction-in-training", "topics", interaction.id],
    queryFn: () =>
      apiResolver<{
        tickets?: TicketInTraining[];
        conversations?: ConversationInTraining[];
      }>(
        api.get(
          `/platform/interaction-in-training/topics/${encodeURIComponent(
            interaction.topic ?? ""
          )}`
        )
      ),
    enabled: !!interaction.topic,
  });

  const showInTrainingButton = useMemo(() => {
    const ticketsInTraining = data?.tickets;
    const conversationsInTraining = data?.conversations;

    if (source === "tickets") {
      if (!ticketsInTraining || !interaction?.id) return false;

      const ticketInTraining = ticketsInTraining.find(
        (t: TicketInTraining) =>
          t.ticketId === interaction.id &&
          t.status === InteractionInTrainingStatus.PENDING
      );

      return !ticketInTraining;
    } else if (source === "conversations") {
      if (!conversationsInTraining || !interaction?.id) return false;

      const conversationInTraining = conversationsInTraining.find(
        (c: ConversationInTraining) => c.conversationId === interaction.id
      );

      return !conversationInTraining;
    }
  }, [data?.tickets, data?.conversations, interaction?.id]);

  if (isLoading) {
    return <Spinner size={"2"} />;
  }

  if (!showInTrainingButton) {
    return (
      <Badge size={"2"} color={"blue"} variant={"surface"}>
        In Training
      </Badge>
    );
  }

  return (
    <SendInteractionToTraining
      interactionType={
        source === "tickets"
          ? InteractionType.TICKET
          : InteractionType.CONVERSATION
      }
      interactionId={interaction.id}
      refetch={refetch}
    />
  );
}
